import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Navigate } from 'react-router-dom';
import { 
  Settings, 
  Users, 
  Key, 
  Clock, 
  Trash2, 
  UserPlus, 
  Shield, 
  AlertCircle,
  Check,
  X,
  Loader2,
  Search,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Copy
} from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '../api/client';
import { useAuthStore } from '../stores/auth.store';
import AppShell from '../components/layout/AppShell';

type TabId = 'oauth' | 'trial' | 'users';

export default function AdminPage() {
  const { user } = useAuthStore();
  const [activeTab, setActiveTab] = useState<TabId>('oauth');
  
  // Only admins can access
  if (!user || (user.role !== 'admin' && user.role !== 'superadmin')) {
    return <Navigate to="/dashboard" replace />;
  }
  
  const tabs = [
    { id: 'oauth' as TabId, label: 'OAuth Provider', icon: Key },
    { id: 'trial' as TabId, label: 'Trial Settings', icon: Clock },
    { id: 'users' as TabId, label: 'Users', icon: Users },
  ];
  
  return (
    <AppShell>
      <div className="flex-1 overflow-hidden flex flex-col">
        {/* Header */}
        <div className="border-b border-border-default px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Shield className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h1 className="text-h2 text-text-primary">Admin Panel</h1>
              <p className="text-sm text-text-muted">Systemeinstellungen und Benutzerverwaltung</p>
            </div>
          </div>
        </div>
        
        {/* Tabs */}
        <div className="border-b border-border-default">
          <div className="flex gap-1 px-6">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-primary text-text-primary'
                    : 'border-transparent text-text-muted hover:text-text-secondary'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'oauth' && <OAuthSettings />}
          {activeTab === 'trial' && <TrialSettings />}
          {activeTab === 'users' && <UserManagement />}
        </div>
      </div>
    </AppShell>
  );
}

// =============================================
// OAuth Settings Tab
// =============================================
function OAuthSettings() {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    microsoft_client_id: '',
    microsoft_client_secret: '',
    google_client_id: '',
    google_client_secret: '',
  });
  
  const { data, isLoading } = useQuery({
    queryKey: ['admin-settings'],
    queryFn: () => adminApi.getSettings(),
  });
  
  const { data: statusData } = useQuery({
    queryKey: ['oauth-status'],
    queryFn: () => adminApi.getOAuthStatus(),
  });
  
  const mutation = useMutation({
    mutationFn: adminApi.updateOAuthSettings,
    onSuccess: () => {
      toast.success('OAuth-Einstellungen gespeichert');
      queryClient.invalidateQueries({ queryKey: ['admin-settings'] });
      queryClient.invalidateQueries({ queryKey: ['oauth-status'] });
    },
    onError: () => {
      toast.error('Fehler beim Speichern');
    },
  });
  
  useEffect(() => {
    if (data?.settings) {
      setFormData({
        microsoft_client_id: data.settings.microsoft_client_id || '',
        microsoft_client_secret: data.settings.microsoft_client_secret || '',
        google_client_id: data.settings.google_client_id || '',
        google_client_secret: data.settings.google_client_secret || '',
      });
    }
  }, [data]);
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutation.mutate(formData);
  };
  
  const baseUrl = window.location.origin;
  const microsoftRedirectUri = `${baseUrl}/api/auth/callback/microsoft`;
  const googleRedirectUri = `${baseUrl}/api/auth/callback/google`;
  
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('In Zwischenablage kopiert');
  };
  
  if (isLoading) {
    return <LoadingSpinner />;
  }
  
  return (
    <form onSubmit={handleSubmit} className="space-y-8 max-w-2xl">
      {/* Status */}
      <div className="flex items-center gap-4 p-4 rounded-lg bg-bg-surface border border-border-default">
        <div className="flex items-center gap-2">
          <span className="text-sm text-text-muted">Microsoft:</span>
          {statusData?.microsoft ? (
            <span className="flex items-center gap-1 text-sm text-green-500">
              <Check className="w-4 h-4" /> Konfiguriert
            </span>
          ) : (
            <span className="flex items-center gap-1 text-sm text-text-muted">
              <X className="w-4 h-4" /> Nicht konfiguriert
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-text-muted">Google:</span>
          {statusData?.google ? (
            <span className="flex items-center gap-1 text-sm text-green-500">
              <Check className="w-4 h-4" /> Konfiguriert
            </span>
          ) : (
            <span className="flex items-center gap-1 text-sm text-text-muted">
              <X className="w-4 h-4" /> Nicht konfiguriert
            </span>
          )}
        </div>
      </div>
      
      {/* Microsoft OAuth */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-text-primary flex items-center gap-2">
            <svg className="w-5 h-5" viewBox="0 0 21 21" fill="currentColor">
              <path d="M0 0h10v10H0V0zm11 0h10v10H11V0zM0 11h10v10H0V11zm11 0h10v10H11V11z"/>
            </svg>
            Microsoft 365 / Outlook
          </h3>
          <a 
            href="https://portal.azure.com/#view/Microsoft_AAD_RegisteredApps/ApplicationsListBlade" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-sm text-primary hover:underline flex items-center gap-1"
          >
            Azure Portal <ExternalLink className="w-3 h-3" />
          </a>
        </div>
        
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1">
              Client ID (Application ID)
            </label>
            <input
              type="text"
              value={formData.microsoft_client_id}
              onChange={(e) => setFormData(prev => ({ ...prev, microsoft_client_id: e.target.value }))}
              placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
              className="w-full px-3 py-2 rounded-lg bg-bg-input border border-border-default text-text-primary placeholder:text-text-disabled focus:border-primary focus:outline-none"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1">
              Client Secret
            </label>
            <input
              type="password"
              value={formData.microsoft_client_secret}
              onChange={(e) => setFormData(prev => ({ ...prev, microsoft_client_secret: e.target.value }))}
              placeholder="••••••••"
              className="w-full px-3 py-2 rounded-lg bg-bg-input border border-border-default text-text-primary placeholder:text-text-disabled focus:border-primary focus:outline-none"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1">
              Redirect URI (für Azure Portal)
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={microsoftRedirectUri}
                readOnly
                className="flex-1 px-3 py-2 rounded-lg bg-bg-surface border border-border-default text-text-muted"
              />
              <button
                type="button"
                onClick={() => copyToClipboard(microsoftRedirectUri)}
                className="px-3 py-2 rounded-lg bg-bg-surface border border-border-default text-text-secondary hover:bg-bg-surface-hover"
              >
                <Copy className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Google OAuth */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-text-primary flex items-center gap-2">
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Google / Gmail
          </h3>
          <a 
            href="https://console.cloud.google.com/apis/credentials" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-sm text-primary hover:underline flex items-center gap-1"
          >
            Google Cloud Console <ExternalLink className="w-3 h-3" />
          </a>
        </div>
        
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1">
              Client ID
            </label>
            <input
              type="text"
              value={formData.google_client_id}
              onChange={(e) => setFormData(prev => ({ ...prev, google_client_id: e.target.value }))}
              placeholder="xxxxxxxxxxxx-xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx.apps.googleusercontent.com"
              className="w-full px-3 py-2 rounded-lg bg-bg-input border border-border-default text-text-primary placeholder:text-text-disabled focus:border-primary focus:outline-none"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1">
              Client Secret
            </label>
            <input
              type="password"
              value={formData.google_client_secret}
              onChange={(e) => setFormData(prev => ({ ...prev, google_client_secret: e.target.value }))}
              placeholder="••••••••"
              className="w-full px-3 py-2 rounded-lg bg-bg-input border border-border-default text-text-primary placeholder:text-text-disabled focus:border-primary focus:outline-none"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1">
              Redirect URI (für Google Console)
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={googleRedirectUri}
                readOnly
                className="flex-1 px-3 py-2 rounded-lg bg-bg-surface border border-border-default text-text-muted"
              />
              <button
                type="button"
                onClick={() => copyToClipboard(googleRedirectUri)}
                className="px-3 py-2 rounded-lg bg-bg-surface border border-border-default text-text-secondary hover:bg-bg-surface-hover"
              >
                <Copy className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Info */}
      <div className="flex items-start gap-3 p-4 rounded-lg bg-blue-500/10 border border-blue-500/20">
        <AlertCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-text-secondary">
          <p className="font-medium text-text-primary mb-1">Hinweis</p>
          <p>Mindestens ein OAuth-Provider muss konfiguriert sein, damit Benutzer E-Mail-Konten verbinden können. Die Secrets werden verschlüsselt gespeichert.</p>
        </div>
      </div>
      
      {/* Submit */}
      <div className="flex justify-end">
        <button
          type="submit"
          disabled={mutation.isPending}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-white font-medium hover:bg-primary/90 disabled:opacity-50"
        >
          {mutation.isPending ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Check className="w-4 h-4" />
          )}
          Speichern
        </button>
      </div>
    </form>
  );
}

// =============================================
// Trial Settings Tab
// =============================================
function TrialSettings() {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    trial_duration_days: 14,
    max_mail_accounts: 3,
  });
  
  const { data, isLoading } = useQuery({
    queryKey: ['admin-settings'],
    queryFn: () => adminApi.getSettings(),
  });
  
  const mutation = useMutation({
    mutationFn: adminApi.updateTrialSettings,
    onSuccess: () => {
      toast.success('Einstellungen gespeichert');
      queryClient.invalidateQueries({ queryKey: ['admin-settings'] });
    },
    onError: () => {
      toast.error('Fehler beim Speichern');
    },
  });
  
  useEffect(() => {
    if (data?.settings) {
      setFormData({
        trial_duration_days: data.settings.trial_duration_days || 14,
        max_mail_accounts: data.settings.max_mail_accounts || 3,
      });
    }
  }, [data]);
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutation.mutate(formData);
  };
  
  if (isLoading) {
    return <LoadingSpinner />;
  }
  
  return (
    <form onSubmit={handleSubmit} className="space-y-6 max-w-md">
      <div>
        <label className="block text-sm font-medium text-text-secondary mb-1">
          Trial-Dauer (Tage)
        </label>
        <input
          type="number"
          min="1"
          max="365"
          value={formData.trial_duration_days}
          onChange={(e) => setFormData(prev => ({ ...prev, trial_duration_days: parseInt(e.target.value) || 14 }))}
          className="input"
        />
        <p className="mt-1 text-sm text-text-muted">
          Wie lange neue Benutzer kostenlos testen können.
        </p>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-text-secondary mb-1">
          Max. E-Mail-Konten pro User
        </label>
        <input
          type="number"
          min="1"
          max="20"
          value={formData.max_mail_accounts}
          onChange={(e) => setFormData(prev => ({ ...prev, max_mail_accounts: parseInt(e.target.value) || 3 }))}
          className="input"
        />
        <p className="mt-1 text-sm text-text-muted">
          Wie viele E-Mail-Konten ein Benutzer maximal verbinden kann.
        </p>
      </div>
      
      <div className="flex justify-end">
        <button
          type="submit"
          disabled={mutation.isPending}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-white font-medium hover:bg-primary/90 disabled:opacity-50"
        >
          {mutation.isPending ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Check className="w-4 h-4" />
          )}
          Speichern
        </button>
      </div>
    </form>
  );
}

// =============================================
// User Management Tab
// =============================================
function UserManagement() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [editingUser, setEditingUser] = useState<string | null>(null);
  const [extendDays, setExtendDays] = useState(14);
  
  const { data, isLoading } = useQuery({
    queryKey: ['admin-users', page, search],
    queryFn: () => adminApi.getUsers({ page, limit: 20, search }),
  });
  
  const updateMutation = useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: any }) => 
      adminApi.updateUser(userId, data),
    onSuccess: () => {
      toast.success('User aktualisiert');
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      setEditingUser(null);
    },
    onError: () => {
      toast.error('Fehler beim Aktualisieren');
    },
  });
  
  const deleteMutation = useMutation({
    mutationFn: adminApi.deleteUser,
    onSuccess: () => {
      toast.success('User gelöscht');
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    },
    onError: () => {
      toast.error('Fehler beim Löschen');
    },
  });
  
  const extendMutation = useMutation({
    mutationFn: ({ userId, days }: { userId: string; days: number }) =>
      adminApi.extendTrial(userId, days),
    onSuccess: () => {
      toast.success('Trial verlängert');
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      setEditingUser(null);
    },
    onError: () => {
      toast.error('Fehler beim Verlängern');
    },
  });
  
  const users = data?.users || [];
  const pagination = data?.pagination;
  
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-500/10 text-green-500';
      case 'trial': return 'bg-yellow-500/10 text-yellow-500';
      case 'suspended': return 'bg-red-500/10 text-red-500';
      default: return 'bg-gray-500/10 text-gray-500';
    }
  };
  
  const getRoleColor = (role: string) => {
    switch (role) {
      case 'superadmin': return 'bg-purple-500/10 text-purple-500';
      case 'admin': return 'bg-blue-500/10 text-blue-500';
      default: return 'bg-gray-500/10 text-gray-500';
    }
  };
  
  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
        <input
          type="text"
          placeholder="Suche nach E-Mail oder Name..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          className="w-full pl-10 pr-4 py-2 rounded-lg bg-bg-input border border-border-default text-text-primary placeholder:text-text-disabled focus:border-primary focus:outline-none"
        />
      </div>
      
      {/* Users Table */}
      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border-default">
          <table className="w-full">
            <thead className="bg-bg-surface">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-text-muted">User</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-text-muted">Status</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-text-muted">Rolle</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-text-muted">Trial endet</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-text-muted">Letzter Login</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-text-muted">Aktionen</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-default">
              {users.map((user) => (
                <tr key={user.id} className="hover:bg-bg-surface/50">
                  <td className="px-4 py-3">
                    <div>
                      <div className="font-medium text-text-primary">{user.displayName || '—'}</div>
                      <div className="text-sm text-text-muted">{user.email}</div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${getStatusColor(user.status)}`}>
                      {user.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${getRoleColor(user.role)}`}>
                      {user.role}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-text-secondary">
                    {user.trialEndsAt 
                      ? new Date(user.trialEndsAt).toLocaleDateString('de-DE')
                      : '—'
                    }
                  </td>
                  <td className="px-4 py-3 text-sm text-text-secondary">
                    {user.lastLoginAt 
                      ? new Date(user.lastLoginAt).toLocaleDateString('de-DE')
                      : '—'
                    }
                  </td>
                  <td className="px-4 py-3">
                    {user.role !== 'superadmin' && (
                      <div className="flex items-center justify-end gap-2">
                        {/* Edit dropdown */}
                        {editingUser === user.id ? (
                          <div className="flex items-center gap-2">
                            <select
                              onChange={(e) => {
                                if (e.target.value === 'extend') {
                                  extendMutation.mutate({ userId: user.id, days: extendDays });
                                } else if (e.target.value) {
                                  updateMutation.mutate({ 
                                    userId: user.id, 
                                    data: { status: e.target.value } 
                                  });
                                }
                              }}
                              className="px-2 py-1 rounded bg-bg-input border border-border-default text-sm text-text-primary"
                              defaultValue=""
                            >
                              <option value="" disabled>Aktion...</option>
                              <option value="active">→ Active</option>
                              <option value="trial">→ Trial</option>
                              <option value="suspended">→ Suspended</option>
                              <option value="extend">Trial +{extendDays} Tage</option>
                            </select>
                            <button
                              onClick={() => setEditingUser(null)}
                              className="p-1 text-text-muted hover:text-text-secondary"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <>
                            <button
                              onClick={() => setEditingUser(user.id)}
                              className="p-1.5 rounded hover:bg-bg-surface text-text-muted hover:text-text-secondary"
                              title="Bearbeiten"
                            >
                              <Settings className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => {
                                if (confirm(`User "${user.email}" wirklich löschen?`)) {
                                  deleteMutation.mutate(user.id);
                                }
                              }}
                              className="p-1.5 rounded hover:bg-red-500/10 text-text-muted hover:text-red-500"
                              title="Löschen"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      
      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-text-muted">
            {pagination.total} User insgesamt
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-1.5 rounded hover:bg-bg-surface disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-sm text-text-secondary">
              Seite {page} von {pagination.totalPages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))}
              disabled={page === pagination.totalPages}
              className="p-1.5 rounded hover:bg-bg-surface disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================
// Helper Components
// =============================================
function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center py-12">
      <Loader2 className="w-8 h-8 animate-spin text-primary" />
    </div>
  );
}
